import React from 'react';
import { merge, Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap, throttleTime } from 'rxjs/operators';
import { Nullable } from '../Shared/Types';

type FetchArguments = Array<string | number | boolean | object | undefined | null>;

export type AsyncFetchRenderProps<PayloadType> = {
    loading: boolean;
    loadingErrors: string;
    data: Nullable<PayloadType>;
    loadCount: number;
    updateData(values: PayloadType): void;
    refresh(...params: FetchArguments): Promise<void>;
    reset(): void;
};

type AsyncFetchProps<PayloadType> = {
    defaultValue?: PayloadType;
    preventInitialLoad?: boolean;
    debounceDuration?: number;
    fetch(...params: FetchArguments): Promise<PayloadType>;
    onError?(props: AsyncFetchRenderProps<PayloadType>): void;
    renderError?(props: AsyncFetchRenderProps<PayloadType>): React.ReactNode;
    render(props: AsyncFetchRenderProps<PayloadType>): React.ReactNode;
};

export class AsyncFetch<PayloadType = any> extends React.Component<
    AsyncFetchProps<PayloadType>,
    AsyncFetchRenderProps<PayloadType>
> {
    private submission$: Subject<FetchArguments | null>;
    private debouncedSubmission$: Subject<void>;

    private submission$$: Nullable<Subscription> = null;
    private activateSubmitting$$: Nullable<Subscription> = null;

    constructor(props: AsyncFetchProps<PayloadType>) {
        super(props);

        this.submission$ = new Subject();
        this.debouncedSubmission$ = new Subject();

        this.state = {
            loading: false,
            loadingErrors: '',
            data: this.props.defaultValue ? this.props.defaultValue : null,
            refresh: this.refresh,
            reset: this.reset,
            updateData: this.updateData,
            loadCount: 0
        };
    }

    public componentDidMount() {
        this.activateSubmitting$$ = merge(this.submission$, this.debouncedSubmission$).subscribe(() => {
            this.setState({
                loading: true
            });
        });

        this.submission$$ = merge(
            this.submission$.pipe(throttleTime(500)),
            this.debouncedSubmission$.pipe(debounceTime(500))
        )
            .pipe(
                switchMap(async (args: FetchArguments | null | undefined | void) => {
                    if (!!args) return this.props.fetch(...args);

                    return this.props.fetch();
                })
            )
            .subscribe(
                (data: PayloadType) => {
                    this.setState((prevState: AsyncFetchRenderProps<PayloadType>) => ({
                        loading: false,
                        data,
                        loadingErrors: '',
                        loadCount: ++prevState.loadCount
                    }));
                },
                (err: any) => {
                    this.setState(
                        (
                            previousState: AsyncFetchRenderProps<PayloadType>,
                            setStateProps: AsyncFetchProps<PayloadType>
                        ) => ({
                            loading: false,
                            loadingErrors: err && err.message ? err.message : err,
                            loadCount: ++previousState.loadCount,
                            data: setStateProps.defaultValue ? setStateProps.defaultValue : null
                        }),
                        this.onErrorSetStateComplete
                    );
                }
            );

        if (!this.props.preventInitialLoad) this.submission$.next();
    }

    public componentWillUnmount(): void {
        if (this.submission$$ && this.submission$$.unsubscribe) this.submission$$.unsubscribe();

        if (this.activateSubmitting$$ && this.activateSubmitting$$.unsubscribe) this.activateSubmitting$$.unsubscribe();
    }

    public render() {
        if (this.state.loadingErrors && this.props.renderError) return this.props.renderError(this.state);

        return this.props.render(this.state);
    }

    private refresh = async (...args: FetchArguments) => {
        this.submission$.next(args);
    };

    private reset = async () => {
        this.setState({ data: this.props.defaultValue ? this.props.defaultValue : null });
    };

    private onErrorSetStateComplete = () => {
        if (this.props.onError) this.props.onError(this.state);
    };

    private updateData = (data: PayloadType) => {
        this.setState({ data });
    };
}
